import PropTypes from 'prop-types';

import MarkdownWrapper from 'core/components/MarkdownWrapper';
import withTheme from 'core/hocs/withTheme';
import Link from 'core/components/Link';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import themePropTypes from 'core/utils/prop-types/theme';

import styles from './index.styl';

/**
 * @author Aleksandr Bunin <al.bunin@rambler-co.ru>
 */
function CaptionCredits(props) {
  const {
    className,
    caption,
    credits,
    creditsPrefix,
    position,
    captionPrefix,
    creditsUrl,
    theme: {
      controls: {
        captionCredits: captionCreditsAtoms,
      },
    },
    captionStyles,
    creditsStyles,
  } = props;

  if (!caption && !credits) {
    return null;
  }

  const captionAtoms = { ...captionCreditsAtoms.caption, ...(captionStyles || {}) };
  const creditsAtoms = { ...captionCreditsAtoms.credits, ...(creditsStyles || {}) };

  const {
    font: captionFont,
    color: captionColor,
    margin: captionMargin,
  } = captionAtoms;

  const {
    margin: creditsMargin,
    font: creditsFont,
    color: creditsColor,
  } = creditsAtoms;

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .info
          padding ${captionCreditsAtoms.padding}
          background ${captionCreditsAtoms.background}
          text-align ${position ? position : captionCreditsAtoms.textAlign}
          max-width ${captionCreditsAtoms.maxWidth}
          margin ${captionCreditsAtoms.margin}

        .${styles.caption}
          margin ${captionMargin}
          font ${captionFont}
          color ${captionColor}

        .${styles.credits}
          margin ${creditsMargin}
          font ${creditsFont}
          color ${creditsColor}
      `}</style>
      <style jsx>{`
        .info
          flex auto

        .creditsUrl
          font inherit
          color inherit
          text-decoration none
          &:visited
          &:active
            text-decoration none
          &:hover
            text-decoration underline
      `}</style>
    </scope>
  );

  const creditsAuthorship = `${creditsPrefix ? `${creditsPrefix} ` : ''}${credits}`;

  return (
    <div className={scoped.wrapClassNames('info', className)}>
      {caption && <div className={scoped.wrapClassNames(styles.caption)}>
        {captionPrefix()}
        <MarkdownWrapper>
          {caption}
        </MarkdownWrapper>
      </div>}
      {credits && <div className={scoped.wrapClassNames(styles.credits)}>
        {creditsUrl ? (
          <Link
            className={scoped.wrapClassNames('creditsUrl')}
            to={creditsUrl}
            type='blank'
          >
            {creditsAuthorship}
          </Link>
        ) : <MarkdownWrapper>{creditsAuthorship}</MarkdownWrapper>}
      </div>}
      <scoped.styles />
    </div>
  );
}

CaptionCredits.propTypes = {
  /**
   * Кастомный класс
   */
  className: PropTypes.string,
  /**
   * Подпись к изображению
   */
  caption: PropTypes.string,
  /**
   * Информация об источнике
   */
  credits: PropTypes.string,
  /**
   * Префикс к информации об источнике
   */
  creditsPrefix: PropTypes.string,
  /**
   *  Ссылка на источник
   */
  creditsUrl: PropTypes.string,
  /**
   * Управляет CSS свойством `text-align`.
   * Переопределяет значение из темы.
   */
  position: PropTypes.string,
  /*
   * Префикс для подписи к изображению.
   *
   * Наприммер в виджете sideBySide:
   * - Слева:
   * - Справа:
  **/
  captionPrefix: PropTypes.func,
  /**
   * Стили caption для переопределения значений из темы
   */
  captionStyles: PropTypes.shape({
    font: PropTypes.string,
    color: PropTypes.string,
    margin: PropTypes.string,
  }),
  /**
   * Стили credits для переопределения значений из темы
   */
  creditsStyles: PropTypes.shape({
    font: PropTypes.string,
    color: PropTypes.string,
    margin: PropTypes.string,
  }),
  /** @ignore */
  theme: themePropTypes(`{
    controls: {
      captionCredits: {
        textAlign,
        background,
        padding,
        caption: {
          font,
          color,
        },
        credits: {
          font,
          color,
          margin,
        },
      },
    },
  }`),
};

CaptionCredits.defaultProps = {
  captionPrefix: () => null,
  creditsPrefix: '©',  // оставляем в таком ввиде, так как '&copy;' в некоторых местах применяется некорректно
};

const CaptionCreditsWithHOCs = withTheme(CaptionCredits);
CaptionCreditsWithHOCs.displayName = 'CaptionCredits';

export { CaptionCredits as StorybookComponent };
export default CaptionCreditsWithHOCs;
